// ─────────────────────────────────────────────────────────────────────────────
// RecentRelease, CD's "Most recent release" card. Populated from the composited
// RecentReleaseDetail (lib fetchRecentRelease: releases blob + /release-analytics
// + songstats + fresh streams/popularity). Each null stat renders "-" muted
// (Bible 06.4). Empty: "No releases yet" + the .release-empty note, exactly as
// dashboard-empty.html.
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import type { RecentReleaseDetail } from '../../lib';
import { fmt, fmtShort, longDate, relativeReleaseLabel, shortDate } from './format';

export interface RecentReleaseProps {
  readonly release: RecentReleaseDetail | null;
}

/** One stat row, renders muted "-" when the value is null. */
function RStat({ label, value, short }: { label: string; value: number | null; short?: boolean }): JSX.Element {
  const has = value != null;
  const text = has ? (short ? fmtShort(value) : fmt(value)) : '-';
  return (
    <div className="rstat">
      <span className="rstat__label">{label}</span>
      <span className={'rstat__num' + (has ? '' : ' rstat__num--muted')}>{text}</span>
    </div>
  );
}

export function RecentRelease({ release }: RecentReleaseProps): JSX.Element {
  if (!release) {
    // ── EMPTY (dashboard-empty.html #label-recent-release) ──
    return (
      <article className="card release-card" id="label-recent-release">
        <header className="release-card__head">
          <div>
            <p className="eyebrow">Most recent release</p>
            <h2 className="release-card__title disp">No releases yet</h2>
            <p className="release-card__sub">
              Add a release and your latest drop shows here with full streaming + rollout signals.
            </p>
          </div>
        </header>
        <div className="release-empty">
          <span className="release-empty__glyph" aria-hidden="true">
            ♪
          </span>
          <p className="empty-note">
            No releases on file yet. Add one on the Releases tab and it'll appear here with
            day-in-cycle, streams, playlist reach and more.
          </p>
        </div>
      </article>
    );
  }

  // ── POPULATED ──
  const popularity =
    release.spotifyPopularity != null ? release.spotifyPopularity + '/100' : '-';

  return (
    <article className="card release-card" id="label-recent-release">
      <header className="release-card__head">
        <div>
          <p className="eyebrow">Most recent release</p>
          <h2 className="release-card__title disp">{release.title}</h2>
          <p className="release-card__sub">
            Streaming performance and rollout signals for the band's latest drop.
          </p>
        </div>
        {release.upcoming && (
          <Link className="upcoming-pill" to="/releases">
            <span className="upcoming-pill__eyebrow">Upcoming</span>
            <span className="upcoming-pill__row">
              <span
                className="upcoming-pill__art"
                aria-hidden="true"
                style={
                  release.upcoming.artworkUrl
                    ? { backgroundImage: `url(${release.upcoming.artworkUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                    : undefined
                }
              ></span>
              <span className="upcoming-pill__text">
                <span className="upcoming-pill__title">{release.upcoming.title}</span>
                <span className="upcoming-pill__meta">{shortDate(release.upcoming.date) || 'TBA'}</span>
              </span>
            </span>
          </Link>
        )}
      </header>

      <div className="release-card__body">
        {/* Cover artwork, CD's placeholder; build feeds release.artworkUrl. */}
        <div
          className="release-art"
          aria-hidden="true"
          style={
            release.artworkUrl
              ? { backgroundImage: `url(${release.artworkUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
              : undefined
          }
        >
          {!release.artworkUrl && <span className="release-art__glyph">♪</span>}
        </div>

        <div className="release-detail">
          <div className="release-meta">
            <span className="release-meta__type">{release.type}</span>
            <span>·</span>
            <span>{release.isUpcoming ? '\u2014' : longDate(release.releaseDate)}</span>
            <span>·</span>
            <span className="release-meta__rel">
              {release.isUpcoming ? 'Upcoming' : relativeReleaseLabel(release.releaseDate)}
            </span>
          </div>

          <div className="release-stats">
            <div className="rstat">
              <span className="rstat__label">Day in cycle</span>
              <span className={'rstat__num' + (release.dayInCycle != null ? '' : ' rstat__num--muted')}>
                {release.dayInCycle != null ? 'D' + release.dayInCycle : '-'}
              </span>
            </div>
            <RStat label="Landing visits · MTD" value={release.landingVisitsMtd} short />
            <RStat label="Smart-link clicks · MTD" value={release.smartLinkClicksMtd} />
            <RStat label="Spotify streams" value={release.spotifyStreams} short />
            <div className="rstat">
              <span className="rstat__label">Spotify popularity</span>
              <span
                className={'rstat__num' + (release.spotifyPopularity != null ? '' : ' rstat__num--muted')}
              >
                {popularity}
              </span>
            </div>
            <RStat label="Playlists" value={release.playlistsTotal} />
            <RStat label="Playlist reach" value={release.playlistReach} short />
            <RStat label="Editorial picks" value={release.editorialPicks} />
          </div>
        </div>
      </div>
    </article>
  );
}
